/**
 * Page Queries - getPageBySlug uses slim fragment for lazy-load (Contentful 8KB limit).
 *
 * @override
 * - Re-exports all base page queries from @amblique/contentful
 * - Overrides getPageBySlug to use PageFragmentSlim so the initial query stays under 8KB;
 *   each region block then fetches full data via Component useXById hooks.
 */

import gql from 'graphql-tag';

import {FullPageFragment} from '@amblique/contentful/app/contentful/fragments/page/full-page.fragment';
import {PageFragmentSlim} from '@amblique/king-living/app/contentful/fragments/page/page-slim.fragment';

// Re-export all base queries (getPageBySlug + getPageById are overridden below)
export {
  getBasePageById,
  getFullPageById,
  getBasePageBySlug,
  getFullPageBySlug,
  getBasePages,
  getPages,
  getFullPages,
} from '@amblique/contentful/app/contentful/queries/page.queries';

/**
 * Get a single Page by slug (slim - lazy-loads each region block via useXById hooks).
 * Keeps query under Contentful 8KB limit; Component auto-fetches full data when incomplete.
 */
export const getPageBySlug = gql`
  ${PageFragmentSlim}

  query GetPageBySlug($slug: String!, $locale: String!, $preview: Boolean) {
    pageCollection(
      where: { slug: $slug }
      limit: 1
      locale: $locale
      preview: $preview
    ) {
      items {
        ...PageFragmentSlim
      }
    }
  }
`;

/**
 * Get a single Page by entry id (slim - lazy-loads each region block via useXById hooks).
 *
 * @override of the base `getPageById`. Mirrors `getPageBySlug`: keeps the query under
 * Contentful's ~8KB limit. The base `getPageById` uses the full `PageFragment`, which
 * exceeds that limit and fails — so page-by-id (e.g. slug-less preview pages) must use
 * this slim query instead. (KL-302 preview)
 */
export const getPageById = gql`
  ${PageFragmentSlim}

  query GetPageById($id: String!, $locale: String!, $preview: Boolean) {
    page(id: $id, locale: $locale, preview: $preview) {
      ...PageFragmentSlim
    }
  }
`;

/** Fetch up to 20 pages by slug for region filtering (Contentful cannot filter on array fields). */
export const getPageBySlugForRegion = gql`
  ${PageFragmentSlim}

  query GetPageBySlugForRegion($slugs: [String]!, $locale: String!, $preview: Boolean) {
    pageCollection(
      where: { slug_in: $slugs }
      limit: 10
      locale: $locale
      preview: $preview
    ) {
      items {
        ...PageFragmentSlim
      }
    }
  }
`;

/** Fetch up to 20 full pages by slug for region filtering. */
export const getFullPageBySlugForRegion = gql`
  ${FullPageFragment}

  query GetFullPageBySlugForRegion($slugs: [String]!, $locale: String!, $preview: Boolean) {
    pageCollection(
      where: { slug_in: $slugs }
      limit: 10
      locale: $locale
      preview: $preview
    ) {
      items {
        ...FullPageFragment
      }
    }
  }
`;
